
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GitMerge, Upload, Rocket, FileText, Plus } from 'lucide-react';

interface TimelineProps {
  componentId?: string;
}

interface ComponentEvent {
  id: string;
  id_component: string;
  type: string;
  description: string;
  event_date: string;
  origin: string;
  created_at: string;
}

const getEventIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'merge request':
      return <GitMerge className="h-4 w-4 text-[#189ab4]" />;
    case 'push':
      return <Upload className="h-4 w-4 text-[#189ab4]" />;
    case 'deploys em produção':
      return <Rocket className="h-4 w-4 text-[#189ab4]" />;
    case 'documentação':
      return <FileText className="h-4 w-4 text-[#189ab4]" />;
    case 'inclusão':
      return <Plus className="h-4 w-4 text-[#189ab4]" />;
    default:
      return <FileText className="h-4 w-4 text-[#189ab4]" />;
  }
};

export function Timeline({ componentId }: TimelineProps) {
  const [originFilter, setOriginFilter] = useState<string>('all');

  const { data: events, isLoading, error } = useQuery({
    queryKey: ['component-events', componentId],
    queryFn: async () => {
      if (!componentId) return [];
      
      // Buscar eventos da tabela component_events
      const { data: componentEvents, error: eventsError } = await supabase
        .from('component_events')
        .select('*')
        .eq('id_component', componentId)
        .order('event_date', { ascending: false });

      if (eventsError) {
        throw eventsError;
      }

      return componentEvents || [];
    },
    enabled: !!componentId,
  });

  // Filtrar eventos por origem
  const filteredEvents = events?.filter(event => {
    if (originFilter === 'all') return true;
    return event.origin.toLowerCase() === originFilter.toLowerCase();
  }) || [];

  if (isLoading) {
    return (
      <Card className="border border-border/40 shadow-sm">
        <CardHeader className="bg-[#05445e] text-white">
          <CardTitle className="text-white">Timeline</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">
            Carregando eventos...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border border-border/40 shadow-sm">
        <CardHeader className="bg-[#05445e] text-white">
          <CardTitle className="text-white">Timeline</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-destructive">
            Erro ao carregar eventos: {(error as Error).message}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-border/40 shadow-sm">
      <CardHeader className="bg-[#05445e] text-white">
        <CardTitle className="text-white">Timeline</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="mb-4">
          <Select value={originFilter} onValueChange={setOriginFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filtrar por origem" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="git">Git</SelectItem>
              <SelectItem value="deploy">Deploy</SelectItem>
              <SelectItem value="mozarch">Mozarch</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!filteredEvents || filteredEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {originFilter === 'all' 
              ? "Nenhum evento encontrado para este componente."
              : "Nenhum evento encontrado para o filtro selecionado."
            }
          </p>
        ) : (
          <div className="space-y-4">
            {filteredEvents.map((event) => (
              <div key={event.id} className="border-l-2 border-[#189ab4] pl-4 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getEventIcon(event.type)}
                    <h4 className="font-medium text-[#05445e]">{event.type}</h4>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(event.event_date), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {event.description}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Origem: {event.origin}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
